import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useThemes, useDeleteTheme, useCreateTheme, type CreateThemeData } from '../hooks/useThemes'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Skeleton } from '@/components/ui/skeleton'
import { toast } from 'sonner'
import { Plus, Trash2, Palette, Download, Lock, Unlock } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'

export function ThemesPage() {
  const navigate = useNavigate()
  const { data: themes, isLoading, error } = useThemes()
  const createTheme = useCreateTheme()
  const deleteTheme = useDeleteTheme()

  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [deleteSlug, setDeleteSlug] = useState<string | null>(null)

  // Form state
  const [formData, setFormData] = useState<CreateThemeData>({
    slug: '',
    name: '',
    current_version: '1.0.0',
    requires_license: false,
    author: 'Jezweb',
  })

  const handleCreate = async () => {
    try {
      await createTheme.mutateAsync(formData)
      toast.success('Theme created successfully')
      setIsCreateOpen(false)
      setFormData({
        slug: '',
        name: '',
        current_version: '1.0.0',
        requires_license: false,
        author: 'Jezweb',
      })
    } catch (err) {
      toast.error('Failed to create theme')
    }
  }

  const handleDelete = async () => {
    if (!deleteSlug) return
    try {
      await deleteTheme.mutateAsync(deleteSlug)
      toast.success('Theme deleted successfully')
      setDeleteSlug(null)
    } catch (err) {
      toast.error('Failed to delete theme')
    }
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-destructive">Failed to load themes: {String(error)}</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold">Themes</h1>
          <p className="text-muted-foreground">Manage WordPress themes on the update server</p>
        </div>
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Add Theme</span>
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Theme</DialogTitle>
              <DialogDescription>
                Register a new theme on the update server.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-4 md:space-y-4">
              <div className="space-y-2">
                <Label htmlFor="slug">Slug</Label>
                <Input
                  id="slug"
                  placeholder="my-theme"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  placeholder="My Theme"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="version">Version</Label>
                <Input
                  id="version"
                  placeholder="1.0.0"
                  value={formData.current_version}
                  onChange={(e) => setFormData({ ...formData, current_version: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="parent_theme">Parent Theme (optional)</Label>
                <Input
                  id="parent_theme"
                  placeholder="hello-elementor"
                  value={formData.parent_theme || ''}
                  onChange={(e) => setFormData({ ...formData, parent_theme: e.target.value || undefined })}
                />
                <p className="text-xs text-muted-foreground">For child themes, enter the parent theme slug</p>
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Requires License</Label>
                  <p className="text-xs text-muted-foreground">
                    Premium theme requiring license key
                  </p>
                </div>
                <Switch
                  checked={formData.requires_license}
                  onCheckedChange={(checked) => setFormData({ ...formData, requires_license: checked })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsCreateOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreate} disabled={createTheme.isPending}>
                {createTheme.isPending ? 'Creating...' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader className="p-4 pb-2 md:p-6 md:pb-2">
          <CardTitle>All Themes</CardTitle>
          <CardDescription>
            {isLoading ? 'Loading...' : `${themes?.length || 0} themes registered`}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-4 pt-0 md:p-6 md:pt-0">
          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : themes?.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Palette className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium">No themes yet</h3>
              <p className="text-muted-foreground">
                Create your first theme to get started
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Theme</TableHead>
                  <TableHead>Version</TableHead>
                  <TableHead>Parent</TableHead>
                  <TableHead>License</TableHead>
                  <TableHead>Downloads</TableHead>
                  <TableHead>Updated</TableHead>
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {themes?.map((theme) => (
                  <TableRow
                    key={theme.slug}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => navigate(`/dashboard/themes/${theme.slug}`)}
                  >
                    <TableCell>
                      <div>
                        <p className="font-medium">{theme.name}</p>
                        <p className="text-sm text-muted-foreground">{theme.slug}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{theme.current_version}</Badge>
                    </TableCell>
                    <TableCell>
                      {theme.parent_theme ? (
                        <Badge variant="secondary">{theme.parent_theme}</Badge>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {theme.requires_license ? (
                        <div className="flex items-center text-amber-600">
                          <Lock className="mr-1 h-4 w-4" />
                          Required
                        </div>
                      ) : (
                        <div className="flex items-center text-green-600">
                          <Unlock className="mr-1 h-4 w-4" />
                          Free
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center">
                        <Download className="mr-1 h-4 w-4 text-muted-foreground" />
                        {theme.total_downloads || 0}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {theme.last_updated
                        ? formatDistanceToNow(new Date(theme.last_updated), { addSuffix: true })
                        : 'Never'}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          asChild
                        >
                          <a
                            href={`/api/update-server/themes/${theme.slug}/download?version=${theme.current_version}`}
                            title={`Download ${theme.name} v${theme.current_version}`}
                          >
                            <Download className="h-4 w-4" />
                          </a>
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDeleteSlug(theme.slug)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteSlug} onOpenChange={() => setDeleteSlug(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Theme</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteSlug}"? This will remove all versions and
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteTheme.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
